/**
 * checkout/messages.ts — server error code → user-facing ES message.
 *
 * Mirrors the table in `docs/checkout-integration-notes.md` § 4. Add new
 * codes here when admin extends the API.
 */

export type ErrorVars = Record<string, string | number>;

// ROUND7 pre-flip: incluyo aliases que admin podría usar literalmente en su
// RESPONSE — OUT_OF_STOCK (sinónimo de INSUFFICIENT_INVENTORY), INVALID_STATE
// (sinónimo de ORDER_INVALID_STATE). Si admin diverge en otros codes, los
// agregamos cuando aparezcan.
const insufficientInventoryMsg = (v?: ErrorVars): string =>
  v?.available !== undefined
    ? `Quedan ${v.available} disponibles. Ajustá tu selección.`
    : 'No hay suficientes disponibles para tu selección.';

const orderInvalidStateMsg = (): string => 'Esta orden ya no puede pagarse. Refrescá la página.';

const MESSAGES: Record<string, (vars?: ErrorVars) => string> = {
  INSUFFICIENT_INVENTORY: insufficientInventoryMsg,
  OUT_OF_STOCK: insufficientInventoryMsg,
  SEAT_ALREADY_LOCKED: () => 'Ese asiento se acaba de reservar. Probá otra localidad.',
  CART_EXPIRED: () => 'Tu carrito expiró. Volvé a elegir tus localidades.',
  ORDER_INVALID_STATE: orderInvalidStateMsg,
  INVALID_STATE: orderInvalidStateMsg,
  PROVIDER_UNAVAILABLE: () => 'El medio de pago no está disponible. Probá más tarde.',
  VALIDATION_FAILED: (v) =>
    v?.issues ? `Revisá los datos: ${v.issues}` : 'Revisá los datos del formulario.',
  ORDER_NOT_FOUND: () => 'No encontramos esa orden.',
  IDEMPOTENCY_MISMATCH: () =>
    'Detectamos una solicitud duplicada con datos distintos. Refrescá la página.',
  PAYMENT_DECLINED: (v) =>
    v?.reason ? `El pago fue rechazado: ${v.reason}` : 'El pago fue rechazado.',
  REQUIRES_ACTION: () => 'Necesitamos un paso extra (autenticación de tu banco).',
  RATE_LIMITED: () => 'Demasiados intentos. Esperá unos segundos y volvé a probar.',
  UNAUTHORIZED: () => 'Sesión expirada. Iniciá sesión otra vez.',
  FORBIDDEN: () => 'No tenés permiso para ver esto.',
  INVALID_CART: () => 'El carrito tiene datos inválidos. Volvé a empezar.',
  NOT_IMPLEMENTED: () => 'Este servicio todavía no está disponible. Probá en unos minutos.',
};

const DEFAULT_5XX = 'Algo falló de nuestro lado. Estamos viendo qué pasó.';
const DEFAULT_GENERIC = 'Ocurrió un error inesperado.';

/**
 * Resolve a server error code into a user-friendly ES message.
 * Unknown codes fall back to a generic message (or a 5xx-specific one if
 * the status is in the 500-599 range).
 */
export function messageForCode(
  code: string | null | undefined,
  status?: number,
  vars?: ErrorVars,
): string {
  if (code && MESSAGES[code]) return MESSAGES[code](vars);
  if (status && status >= 500 && status < 600) return DEFAULT_5XX;
  return DEFAULT_GENERIC;
}
